import { GetStaticPaths, GetStaticProps } from "next";
import { GET_ALL_TURFS } from "../../../src/utils/queries";
import { transformTurfData } from "../../../src/utils/transformTurfData";
import {
  createDynamicCityData,
  formatCityName,
  getCityData,
  getCanonicalCitySlug,
} from "../../../src/config/cityData";
import { areaNameToSlug } from "../../../src/utils/turfRoutes";
import {
  ApiArea,
  fetchApiAreasByCity,
  fetchGraphQL,
  resolveApiCityData,
} from "../../../src/utils/cityApi";
import CityPage from "../index";
import { hasVisiblePitches } from "../../../src/utils/turfVisibility";

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const rawCitySlug = Array.isArray(context.params?.city)
    ? context.params.city[0]
    : context.params?.city;
  const citySlug = rawCitySlug ? getCanonicalCitySlug(rawCitySlug) : rawCitySlug;

  const areaSlug = Array.isArray(context.params?.area)
    ? context.params.area[0]
    : context.params?.area;

  if (!citySlug || !areaSlug || citySlug === "noData") {
    return { notFound: true };
  }

  // Alternate spellings and casing variants must not serve a duplicate 200 page
  // that cross-canonicals to the real slug.
  if (rawCitySlug !== citySlug) {
    return {
      redirect: {
        destination: `/${citySlug}/area/${areaSlug}`,
        permanent: true,
      },
    };
  }

  let cityData = getCityData(rawCitySlug as string);
  let turfData: any[] = [];
  let hasError = false;
  let apiCityName = citySlug;
  let matchedArea: ApiArea | null = null;
  let areaDisplayName = areaSlug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  try {
    if (!cityData) {
      cityData = await resolveApiCityData(citySlug as string);
    }

    if (cityData) {
      apiCityName = cityData.name;
    } else {
      apiCityName = formatCityName(citySlug as string);
    }

    const result = await fetchGraphQL<{
      turfs?: {
        turfs?: any[];
      };
    }>(GET_ALL_TURFS, {
      city: apiCityName,
      verificationStatus: "VERIFIED",
    });

    const allTurfs = (result?.turfs?.turfs || [])
      .filter((turf: any) => turf?.hideTurf !== true && hasVisiblePitches(turf))
      .map((turf: any) =>
        transformTurfData(turf, cityData?.name || apiCityName)
      );

    if (cityData?.id && !cityData.id.startsWith("dynamic-")) {
      const cityAreas = await fetchApiAreasByCity(cityData.id);
      matchedArea =
        cityAreas.find((area) => areaNameToSlug(area.name) === areaSlug) || null;
    }

    if (matchedArea?.name) {
      areaDisplayName = matchedArea.name;
    } else {
      const matchedTurf = allTurfs.find(
        (t: any) => areaNameToSlug(t.data?.areaName) === areaSlug
      );
      if (matchedTurf?.data?.areaName) {
        areaDisplayName = matchedTurf.data.areaName;
      }
    }

    turfData = allTurfs.filter((t: any) => {
      if (matchedArea?.id && t.data?.areaId) {
        return t.data.areaId === matchedArea.id;
      }
      return areaNameToSlug(t.data?.areaName) === areaSlug;
    });

    if (!cityData && turfData.length > 0) {
      cityData = createDynamicCityData({
        name: turfData[0]?.data?.city || apiCityName,
      });
    }
  } catch (error) {
    console.error("Error fetching turf data for", citySlug, "area", areaSlug, ":", error);
    hasError = true;
  }

  if (!cityData) {
    return { notFound: true };
  }

  // Empty area listings must 404. Serving 200 + "No Turf Found" for an admin
  // area with zero live turfs is a Google soft-404 (GSC: /pune/area/kothrud).
  if (!hasError && turfData.length === 0) {
    return { notFound: true };
  }

  return {
    props: {
      citySlug: citySlug as string,
      cityData,
      initialTurfData: turfData,
      turfCount: turfData.length,
      hasError,
      areaSlug,
      areaName: areaDisplayName,
    },
    revalidate: 180,
  };
};

export default CityPage;
